import { useOrganisationStore } from 'Global/Organisations/OrganisationsStore';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { useEffect, useRef, useState } from 'react';
import { CustomTicketTypeEntity, EventDetailEntity, EventLabelEntity, FerryTripEntity } from 'Models/Entities';
import { useNavigate, useLocation } from 'react-router-dom';
import { action } from 'mobx';
import * as React from 'react';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import useAsync from 'Hooks/useAsync';
import alertToast from 'Util/ToastifyUtils';
import InfiniteScrollBox from 'Views/Components/_HumanWritten/InfiniteScroll/InfiniteScrollBox';
import If from '../../../If/If';
import { Button } from '../../../Button/Button';
import FeaturedEventsList from '../FeaturedEvents/FeaturedEventsList';
import { EventTabs } from '../EventTabs/EventTabs';
import { observer } from 'mobx-react';
import { SearchStateProps } from './EventCardList';
import useStore from 'Hooks/useStore';
import {
	EventSeriesWithNextOccurrence,
	GetEventSeriesPageInitialData,
	GetFilteredEventSeries,
} from 'Services/Api/_HumanWritten/EventSeriesEntityService';
import { appConfigStore } from '../../../../../Models/AppConfigStore';
import { EventCardFromSeries } from './EventCard';
import { LottieWaveDotLoader } from '../../Lottie/LottieWaveDotLoader';

function EventList({ searchState }: { searchState: SearchStateProps | null }) {
	const orgStore = useOrganisationStore();
	const store = useStore();
	const { isIpad } = useDeviceDetect();

	const [refresh, setRefresh] = useState<boolean>(false);
	const [allExperiencesList, setAllExperiencesList] = useState<EventSeriesWithNextOccurrence[]>([]);
	const [labels, setLabels] = useState<EventLabelEntity[]>([]);

	const [loading, setLoading] = useState<boolean>(true);
	const [hasMore, setHasMore] = useState<boolean>(true);
	const eventToEdit = useRef<EventDetailEntity>(new EventDetailEntity());

	const navigate = useNavigate();
	const location = useLocation();

	// Set document title
	useEffect(() => {
		document.title = 'Experiences';
	}, []);

	useEffect(() => {
		setRefresh(!refresh);
	}, [orgStore.selectedOrganisation]);

	const fetchInitialPageData = async (
		orgId: string | undefined,
		spacesAvailable: number | undefined,
		categories: string[] | undefined,
		startDate: Date | undefined,
		endDate: Date | undefined,
		signal?: AbortSignal,
	) => {
		setLoading(true);
		const initialPageData = await GetEventSeriesPageInitialData(
			orgId,
			spacesAvailable,
			categories,
			startDate,
			endDate,
		);
		if (signal?.aborted) return;
		setAllExperiencesList(initialPageData.allExperiences);
		setLabels(initialPageData.labels);
		setHasMore(initialPageData.allExperiences.length >= 5);
		setLoading(false);
	};

	const fetchMoreExperiences = async () => {
		const moreExperiences = await GetFilteredEventSeries(undefined, allExperiencesList.length, 5, undefined);
		if (moreExperiences.length < 5) {
			setHasMore(false);
		}
		setAllExperiencesList(prevItems => prevItems.concat(moreExperiences));
	};

	const onClickHandler = action((event: React.MouseEvent<HTMLButtonElement>) => {
		eventToEdit.current = new EventDetailEntity({
			name: '',
			description: '',
			hidden: false,
			termsAndConditions: whiteLabelStore.eventTerms ?? '',
			totalCapacity: 0,
			eventLabelId: undefined,
			tags: 'test',
			isFeatured: false,
			ferryTrip: new FerryTripEntity({
				startDate: new Date(),
				startTime: new Date(),
				endDate: new Date(),
				endTime: new Date(),
				adultPassengerPrice: 0,
				childPassengerPrice: 0,
				infantPassengerPrice: 0,
			}),
			customTicketTypes: [
				new CustomTicketTypeEntity({
					singularName: 'Adult',
					pluralName: 'Adults',
					price: 35,
					maxCapacity: 10,
					quantity: 1,
				}),
			],
		});

		navigate('/upcoming-events/create');
	});

	const result = useAsync(
		async (signal: AbortSignal) => {
			await fetchInitialPageData(
				undefined,
				searchState?.people,
				searchState?.categories,
				searchState?.startDate,
				searchState?.endDate,
				signal,
			);
		},
		[searchState?.people, searchState?.categories, searchState?.startDate, searchState?.endDate],
	);

	if (result.type === 'error') {
		alertToast('Error fetching events', 'error');
	}

	return (
		<>
			<div className="events-list__container">
				<If
					condition={store.isManager && !isIpad && location.pathname !== '/' && location.pathname !== '/home'}
				>
					<Button
						className="fh-button add-event-button"
						// display={Display.Outline}
						// colors={Colors.None}
						onClick={e => onClickHandler(e)}
					>
						+ New experience
					</Button>
				</If>
				<If condition={location.pathname === '/home'}>
					<Button
						className="fh-button view-all-button"
						// display={Display.Outline}
						// colors={Colors.None}
						onClick={e => {
							navigate('/upcoming-events');
						}}
					>
						View all experiences
					</Button>
				</If>
				<If condition={!appConfigStore.eventsSimpleListViewEnabled}>
					<FeaturedEventsList refresh={refresh} />
					<EventTabs
						allExperiences={allExperiencesList}
						setAllExperiences={setAllExperiencesList}
						labels={labels}
						searchState={searchState}
						loading={loading}
					/>
				</If>
				<If condition={appConfigStore.eventsSimpleListViewEnabled}>
					<If condition={loading}>
						<div className="nothing-to-show__container">
							<LottieWaveDotLoader />
							<p>Fetching experiences...</p>
						</div>
					</If>
					<If condition={allExperiencesList.length === 0 && !loading}>
						<div className="no-events-to-show">
							<h6>No upcoming events</h6>
						</div>
					</If>
					<If condition={allExperiencesList.length > 0}>
						<InfiniteScrollBox
							id="simple-events-list-container"
							items={allExperiencesList}
							hasMore={hasMore}
							fetchMoreData={fetchMoreExperiences}
							renderItem={(item, index) => <EventCardFromSeries eventSeries={item} index={index} />}
							loader=""
							endMessage=""
							direction="vertical"
						/>
					</If>
				</If>
			</div>
		</>
	);
}

export default observer(EventList);
